import { LegacyQuotePagesPath } from 'app/home/common/services/legacy/quote-pages/models/paths.config';
import { TDateISODate } from 'app/home/common/utils/iso-date-type/iso-date.type';
import { mapLegacyPathToFormKey } from 'app/home/create-quote/form-config/map-legacy-path-to-form-key';
import { SubmittedFieldsState } from 'app/home/create-quote/form-locking/lockable.store';
import { InsuranceStatusValue } from 'app/home/create-quote/forms/insurance-status/insurance-status.types';
import { PolicyTypeValue } from 'app/home/create-quote/forms/policy-type/policy-type.types';
import { ConstructionTypeValue } from 'app/home/create-quote/forms/property-details/construction-type/construction-type.types';
import { ExteriorWallValue } from 'app/home/create-quote/forms/property-details/exterior-wall/exterior-wall.types';
import { FoundationTypeValue } from 'app/home/create-quote/forms/property-details/foundation/foundation.types';
import { PropertyTypeValue } from 'app/home/create-quote/forms/property-details/property-type/property-type.types';
import { RoofShapeValue } from 'app/home/create-quote/forms/property-details/roof-shape/roof-shape.types';
import { RentalNumberValue } from 'app/home/create-quote/forms/rental-number/rental-number.types';
import { QuoteDraftState } from 'app/home/global-store/quote-draft/quote-draft.model';

/**
 * WebMCP `save_property_details` walk table.
 *
 * One entry per Pages API path the tool can answer, in funnel order. The walk itself is
 * API-driven (each save response supplies the next page path); this table only says which
 * tool argument answers a given page, how it patches the QuoteDraftStore, and the
 * plain-language prompt for `remainingQuestions`. A page the API renders that is not in
 * this table (co-applicant, unit-number, …) stops the walk — the user finishes it in the UI.
 *
 * Schema enums are declared here as `satisfies`-checked const arrays so the JSON schema,
 * the store patch, and the form type unions cannot drift apart.
 */

// ---------------------------------------------------------------------------
// Enums — single source of truth for schema + guard + store patch.
// ---------------------------------------------------------------------------

export const POLICY_TYPES = ['homeowners', 'condominium', 'mobile_home', 'landlord'] as const satisfies readonly PolicyTypeValue[];
export const PROPERTY_TYPES = [
  'single_family_detached',
  'townhouse',
  'manufactured_mobile_home',
  'condominium',
  'commercial',
  'multi_family_unknown_num_of_units',
  'land',
] as const satisfies readonly PropertyTypeValue[];
export const INSURANCE_STATUSES = ['currently_insured', 'new_purchase_new_policy', 'new_purchase_new_policy_first_time', 'no_insurance'] as const satisfies readonly InsuranceStatusValue[];
export const ROOF_SHAPES = ['hip', 'flat', 'other'] as const satisfies readonly RoofShapeValue[];
export const CONSTRUCTION_TYPES = ['frame', 'masonry', 'log', 'superior_fire_resistive'] as const satisfies readonly ConstructionTypeValue[];
export const EXTERIOR_WALLS = [
  'brick_solid',
  'traditional_stucco',
  'siding_vinyl',
  'siding_cement_fiber_clapboard',
  'wood_shingle_shake',
  'stone_veneer_natural',
  'metal_corrugated_galvanized',
] as const satisfies readonly ExteriorWallValue[];
export const FOUNDATIONS = ['slab', 'crawlspace', 'elevated_piers_pilings', 'partially_finished_basement', 'elevated_with_enclosure'] as const satisfies readonly FoundationTypeValue[];

// ---------------------------------------------------------------------------
// Walk table
// ---------------------------------------------------------------------------

/** The agent-facing args bag for `save_property_details`. */
export type SavePropertyDetailsArgs = {
  policy_type?: PolicyTypeValue;
  insurance_status?: InsuranceStatusValue;
  /** YYYY — the Pages API (and CIR) treat year_built as a string. */
  year_built?: string;
  /** True when the property has a swimming pool. */
  pool?: boolean;
  /** Kind of structure on the property (distinct from policy_type). */
  property_type?: PropertyTypeValue;
  /** Square footage of the home. */
  sq_ft?: number;
  /** Number of stories in the home. */
  stories?: number;
  /** YYYY */
  roof_year?: string;
  roof_shape?: RoofShapeValue;
  construction_type?: ConstructionTypeValue;
  exterior_wall?: ExteriorWallValue;
  foundation?: FoundationTypeValue;
  /** YYYY-MM-DD */
  purchase_date?: TDateISODate;
  /** Months per year the home is occupied, e.g. 12 for a primary residence. */
  residency?: number;
  /** True when the previous home insurance expired over 30 days ago (asked when insurance_status is no_insurance). */
  insurance_expired_over_30_days?: boolean;
  /** True when the home is a rental. */
  rental_status?: boolean;
  /** Number of rental units, when the home is a rental. */
  rental_status_number?: number;
  /** YYYY-MM-DD */
  policy_start_date?: TDateISODate;
};

/**
 * One tool argument that can answer a page, plus how it patches the store.
 *
 * `storeKey` is the QuoteDraftStore field the arg writes — it doubles as the
 * `submittedFields` field name, so form-locking and `remainingQuestions` see tool saves.
 */
export type FunnelWalkArg = {
  /** The `save_property_details` argument that can answer this question. */
  arg: keyof SavePropertyDetailsArgs;
  /**
   * The Pages API `attribute_name` the page's form renders for this question. The frontend
   * finds its component by this name — it is the join key between a page's components and
   * the questions the UI actually shows (components the form ignores are not questions).
   */
  attribute: string;
  /**
   * The question the UI asks, verbatim from the form's template (its form-card title).
   * The API's component labels differ in places — the template is the source of truth.
   */
  prompt: string;
  /** The QuoteDraftStore field this arg patches — also its `submittedFields` field name. */
  storeKey: keyof QuoteDraftState;
  /** How the arg patches the QuoteDraftStore before the payload is built from it. */
  toStore: (args: SavePropertyDetailsArgs) => Partial<QuoteDraftState>;
};

export type FunnelWalkEntry = {
  /** The Pages API path this entry answers. */
  path: LegacyQuotePagesPath;
  /**
   * The tool arguments that answer this page. Most pages ask one question; the API also has
   * multi-question pages that re-render the *same* path with the next question after each
   * save (e.g. `/quote/property-details/year-built` asks year built, then pool). The walk
   * keeps saving such a page while unconsumed args for it remain, and stops when they run
   * out — that exhaustion rule is also the loop bound, so an echoing page cannot spin it.
   */
  args: readonly FunnelWalkArg[];
};

/**
 * Funnel order. The walk does not hardcode this sequence into its own logic — each save
 * response supplies the next page — but `remainingQuestions` reports in this order and a
 * seeded walk (current page unknown) falls back to it.
 */
export const FUNNEL_WALK: readonly FunnelWalkEntry[] = [
  {
    path: '/quote/policy_type',
    args: [
      {
        arg: 'policy_type',
        attribute: 'policy_type',
        prompt: 'What type of insurance are you looking for (homeowners, condominium, mobile home, or landlord)?',
        storeKey: 'policyType',
        toStore: (args) => ({ policyType: args.policy_type }),
      },
    ],
  },
  {
    path: '/quote/insurance_status',
    args: [
      {
        arg: 'insurance_status',
        attribute: 'current_insurance_status',
        prompt: 'What brings you to Kin today (currently insured, new purchase with a policy, first-time buyer, or no current insurance)?',
        storeKey: 'insuranceStatus',
        toStore: (args) => ({ insuranceStatus: args.insurance_status }),
      },
    ],
  },
  {
    path: '/quote/insurance_status_days',
    args: [
      {
        arg: 'insurance_expired_over_30_days',
        attribute: 'current_insurance_status',
        prompt: 'Did your insurance expire over 30 days ago?',
        storeKey: 'isInsuranceExpiredMoreThan30Days',
        toStore: (args) => ({ isInsuranceExpiredMoreThan30Days: args.insurance_expired_over_30_days }),
      },
    ],
  },
  {
    path: '/quote/property-details/year-built',
    args: [
      {
        arg: 'year_built',
        attribute: 'year_built',
        prompt: 'What year was your home built (YYYY)?',
        storeKey: 'yearBuilt',
        toStore: (args) => ({ yearBuilt: args.year_built ? Number(args.year_built) : undefined }),
      },
      { arg: 'pool', attribute: 'pool', prompt: 'Does your home have a swimming pool?', storeKey: 'pool', toStore: (args) => ({ pool: args.pool }) },
      { arg: 'sq_ft', attribute: 'sq_ft', prompt: 'What is your home’s approximate square footage?', storeKey: 'sqFt', toStore: (args) => ({ sqFt: args.sq_ft }) },
      { arg: 'stories', attribute: 'stories', prompt: 'How many stories does your home have?', storeKey: 'stories', toStore: (args) => ({ stories: args.stories }) },
    ],
  },
  {
    path: '/quote/property-details/property-type',
    args: [
      {
        arg: 'property_type',
        attribute: 'property_type',
        prompt: 'What type of property do you want to insure (single family detached, townhouse, manufactured/mobile home, condominium, commercial, multi-family, or land)?',
        storeKey: 'propertyType',
        toStore: (args) => ({ propertyType: args.property_type }),
      },
    ],
  },
  {
    path: '/quote/property-details/roof-shape',
    args: [{ arg: 'roof_shape', attribute: 'roof_shape', prompt: 'What is the shape of your roof (hip, flat, or other)?', storeKey: 'roofShape', toStore: (args) => ({ roofShape: args.roof_shape }) }],
  },
  {
    path: '/quote/property-details/roof-year',
    args: [
      {
        arg: 'roof_year',
        attribute: 'roof_construction_year',
        prompt: 'What year was your roof built or completely replaced (YYYY)?',
        storeKey: 'roofYear',
        toStore: (args) => ({ roofYear: args.roof_year ? Number(args.roof_year) : undefined }),
      },
    ],
  },
  {
    path: '/quote/property-details/construction-type',
    args: [
      {
        arg: 'construction_type',
        attribute: 'construction_type',
        prompt: 'What is the construction type of your home (frame, masonry, log, or superior fire resistive)?',
        storeKey: 'constructionType',
        toStore: (args) => ({ constructionType: args.construction_type }),
      },
    ],
  },
  {
    path: '/quote/property-details/exterior-wall',
    args: [
      {
        arg: 'exterior_wall',
        attribute: 'exterior_wall_finish',
        prompt: 'What is the exterior wall finish of your home?',
        storeKey: 'exteriorWall',
        toStore: (args) => ({ exteriorWall: args.exterior_wall }),
      },
    ],
  },
  {
    path: '/quote/property-details/foundation',
    args: [
      { arg: 'foundation', attribute: 'foundation', prompt: 'What kind of foundation is your home built on?', storeKey: 'foundationType', toStore: (args) => ({ foundationType: args.foundation }) },
    ],
  },
  {
    path: '/quote/property_information',
    args: [
      {
        arg: 'purchase_date',
        attribute: 'purchase_date',
        prompt: 'When did you purchase this property (YYYY-MM-DD)?',
        storeKey: 'purchaseDate',
        toStore: (args) => ({ purchaseDate: args.purchase_date }),
      },
    ],
  },
  {
    path: '/quote/residency',
    args: [
      {
        arg: 'residency',
        attribute: 'months_occupied',
        prompt: 'Do you plan to live at this property for most of the time (months per year occupied)?',
        storeKey: 'residency',
        toStore: (args) => ({ residency: args.residency }),
      },
    ],
  },
  {
    path: '/quote/rental_status',
    args: [{ arg: 'rental_status', attribute: 'times_rented', prompt: 'Do you plan to rent out this property?', storeKey: 'rentalStatus', toStore: (args) => ({ rentalStatus: args.rental_status }) }],
  },
  {
    path: '/quote/rental_status_number',
    args: [
      {
        arg: 'rental_status_number',
        attribute: 'times_rented',
        prompt: 'How many times do you plan to rent this property over a 12 month period?',
        storeKey: 'rentalNumber',
        toStore: (args) => ({ rentalNumber: args.rental_status_number as RentalNumberValue | undefined }),
      },
    ],
  },
  {
    path: '/quote/policy_start_date',
    args: [
      {
        arg: 'policy_start_date',
        attribute: 'effective_date',
        prompt: 'When would you like your insurance coverage to begin (YYYY-MM-DD)?',
        storeKey: 'policyStartDate',
        toStore: (args) => ({ policyStartDate: args.policy_start_date }),
      },
    ],
  },
];

/** Unordered: Pages path → the walk entry that answers it. Missing = the walk stops there. */
export const WALK_BY_PATH: Readonly<Record<string, FunnelWalkEntry>> = Object.fromEntries(FUNNEL_WALK.map((entry) => [entry.path, entry]));

/**
 * The questions still to answer, in funnel order — derived from the walk table, skipping
 * questions the draft has already answered (per `submittedFields`) and ones the agent just
 * supplied answers for.
 */
export function remainingQuestions(answeredArgs: readonly string[], submittedForms: SubmittedFieldsState = {}): string[] {
  return FUNNEL_WALK.flatMap((entry) => {
    const submittedFields = submittedForms[mapLegacyPathToFormKey(entry.path) ?? entry.path] ?? {};
    return entry.args.filter((arg) => !answeredArgs.includes(arg.arg) && !submittedFields[arg.storeKey]).map((arg) => arg.prompt);
  });
}

// ---------------------------------------------------------------------------
// Arg validation — mirrors the reactive-form validators the UI enforces, so a
// schema-ignoring agent cannot push garbage into the CIR (the API accepts
// unknown values silently, e.g. policy_type 'mansion').
// ---------------------------------------------------------------------------

const YEAR_RE = /^\d{4}$/;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const isRealDate = (value: string): boolean => {
  const [y, m, d] = value.split('-').map(Number);
  const date = new Date(Date.UTC(y!, (m ?? 1) - 1, d!));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m! - 1 && date.getUTCDate() === d;
};

const daysFromToday = (days: number): number => Date.now() + days * 24 * 60 * 60 * 1000;

function validateEnumArg(args: SavePropertyDetailsArgs, arg: keyof SavePropertyDetailsArgs & string, allowed: readonly string[], label: string): string | undefined {
  const value = args[arg];
  if (value === undefined || (allowed as readonly unknown[]).includes(value)) {
    return undefined;
  }
  return `${label} must be one of: ${allowed.join(', ')}.`;
}

function validateYearArg(args: SavePropertyDetailsArgs, arg: 'year_built' | 'roof_year', label: string): string | undefined {
  const value = args[arg];
  if (value === undefined) {
    return undefined;
  }
  if (!YEAR_RE.test(value)) {
    return `${label} must be a 4-digit year, e.g. "1978".`;
  }
  const year = Number(value);
  if (year < 1899 || year > new Date().getFullYear() + 1) {
    return `${label} must be between 1899 and ${new Date().getFullYear() + 1}.`;
  }
  return undefined;
}

function validateDateArg(args: SavePropertyDetailsArgs, arg: 'purchase_date' | 'policy_start_date', label: string, minDaysFromNow: number | undefined, maxDaysFromNow: number): string | undefined {
  const value = args[arg];
  if (value === undefined) {
    return undefined;
  }
  if (!ISO_DATE_RE.test(value) || !isRealDate(value)) {
    return `${label} must be a real date in YYYY-MM-DD format.`;
  }
  const time = new Date(`${value}T00:00:00Z`).getTime();
  if (minDaysFromNow !== undefined && time < daysFromToday(minDaysFromNow)) {
    return `${label} cannot be before ${new Date(daysFromToday(minDaysFromNow)).toISOString().slice(0, 10)}.`;
  }
  if (time > daysFromToday(maxDaysFromNow)) {
    return `${label} must be within the next ${maxDaysFromNow} days.`;
  }
  return undefined;
}

function validateNumberArg(args: SavePropertyDetailsArgs, arg: keyof SavePropertyDetailsArgs & string, label: string, min: number, max: number): string | undefined {
  const value = args[arg];
  if (value === undefined) {
    return undefined;
  }
  if (typeof value !== 'number' || !Number.isInteger(value) || value < min || value > max) {
    return `${label} must be a whole number between ${min} and ${max}.`;
  }
  return undefined;
}

/**
 * Validates every provided `save_property_details` argument the way the UI's reactive forms
 * do. Returns one rejection per invalid field; an empty array means every provided value is
 * valid (omitted fields are fine — the walk simply will not save them).
 */
export function validatePropertyDetailsArgs(args: SavePropertyDetailsArgs): { field: string; reason: string }[] {
  const checks: [keyof SavePropertyDetailsArgs & string, string | undefined][] = [
    ['policy_type', validateEnumArg(args, 'policy_type', POLICY_TYPES, 'policy_type')],
    ['insurance_status', validateEnumArg(args, 'insurance_status', INSURANCE_STATUSES, 'insurance_status')],
    ['property_type', validateEnumArg(args, 'property_type', PROPERTY_TYPES, 'property_type')],
    ['roof_shape', validateEnumArg(args, 'roof_shape', ROOF_SHAPES, 'roof_shape')],
    ['construction_type', validateEnumArg(args, 'construction_type', CONSTRUCTION_TYPES, 'construction_type')],
    ['exterior_wall', validateEnumArg(args, 'exterior_wall', EXTERIOR_WALLS, 'exterior_wall')],
    ['foundation', validateEnumArg(args, 'foundation', FOUNDATIONS, 'foundation')],
    ['year_built', validateYearArg(args, 'year_built', 'year_built')],
    ['roof_year', validateYearArg(args, 'roof_year', 'roof_year')],
    ['sq_ft', validateNumberArg(args, 'sq_ft', 'sq_ft', 10, 10000)],
    ['stories', validateNumberArg(args, 'stories', 'stories', 1, 100)],
    ['residency', validateNumberArg(args, 'residency', 'residency (months per year occupied)', 1, 12)],
    ['rental_status_number', validateEnumArg(args, 'rental_status_number', [0, 2, 5, 9, 10].map(String), 'rental_status_number')],
    ['purchase_date', validateDateArg(args, 'purchase_date', 'purchase_date', undefined, 60)],
    ['policy_start_date', validateDateArg(args, 'policy_start_date', 'policy_start_date', 0, 60)],
    ['pool', typeof args.pool === 'boolean' || args.pool === undefined ? undefined : 'pool must be true or false.'],
    ['rental_status', typeof args.rental_status === 'boolean' || args.rental_status === undefined ? undefined : 'rental_status must be true or false.'],
    [
      'insurance_expired_over_30_days',
      typeof args.insurance_expired_over_30_days === 'boolean' || args.insurance_expired_over_30_days === undefined ? undefined : 'insurance_expired_over_30_days must be true or false.',
    ],
  ];
  return checks.filter(([, reason]) => reason !== undefined).map(([field, reason]) => ({ field, reason: reason! }));
}
